import Text from "../text/Text";
import Button from "../button/Button";
import Divider from "../divider/Divider";
import { Box, Avatar } from "@mui/material";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";
import Dialog from "../dialog/Dialog";
import {
  aboutTxt,
  appCategoryTxt,
  appDetail,
  getBtnTxt,
  launchAppTerms,
  openBtnTxt,
  shareableUrlTxt,
  supportEmailTxt,
} from "../messages";
import { loaderImg } from "../images";
import { CustomLink } from "../link/Link";
import WalletSymbols from "../WalletSymbols";
import { useDispatch, useSelector } from "react-redux";
import {
  openAppDetailModal,
  setAppList,
  setCollectionData,
  setMyAppsData,
} from "../../redux/actions";
import React, { useEffect, useState, useMemo } from "react";
import {
  callAPIInterface,
  commonAnalyticsAPI,
  EVENT_TYPES,
  handleOpenAppDialog,
  removedAppStatus,
} from "../constants";
import CommonSkeleton from "../CommonSkeleton";
import { Tag } from "../tag/Tag";
import { CustomTooltip } from "../tooltip/Tooltip";
import MaintenancePage from "../MaintenancePage";

function AppDetailsDialog() {
  const dispatch = useDispatch();
  const [appInfo, setAppInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [installAppLoader, setInstallAppLoader] = useState(false);
  const [btnLabel, setBtnLabel] = useState();
  const [imgSrc, setImgSrc] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const { isAppDetailOpen, appDetails, collectionData, appList, myApps } =
    useSelector((state) => state.common);

  const { id } = appDetails;

  const {
    display_name,
    description,
    logo_url,
    shareable_url,
    support_email,
    app_info,
    app_categories,
    is_app_installed,
    client_id,
    status,
    under_maintenance,
  } = appInfo;

  useEffect(() => {
    setBtnLabel(is_app_installed ? openBtnTxt : getBtnTxt);
    setIsAppInstalled(is_app_installed);
  }, [is_app_installed]);

  useEffect(() => {
    setImgSrc(logo_url);
  }, [logo_url]);

  const copyToClipboard = (data, setFn, isCallEvent = false) => {
    navigator?.clipboard
      ?.writeText(data)
      .then(() => {
        setFn(true);
        setTimeout(async () => {
          setFn(false);
          if (isCallEvent) {
            await commonAnalyticsAPI({
              data: appInfo,
              type: EVENT_TYPES.SHARED,
            });
          }
        }, 1000);
      })
      .catch(() => {});
  };

  const appDetailsInfo = useMemo(
    () => [
      {
        label: shareableUrlTxt,
        data: shareable_url,
        icon: (
          <Box>
            <CustomTooltip
              arrow={false}
              placement="top"
              text={
                <>
                  <CheckCircleSharpIcon />
                  <span>Copied</span>{" "}
                </>
              }
              open={copied}
            >
              <Box sx={{ cursor: "pointer" }}>
                <WalletSymbols
                  symbol="content_copy"
                  action={(e) => {
                    e.stopPropagation();
                    copyToClipboard(shareable_url, setCopied, true);
                  }}
                  sx={{
                    color: "#aba3a3",
                    pointerEvents: copied ? "none" : "auto",
                  }}
                />
              </Box>
            </CustomTooltip>
          </Box>
        ),
      },
      {
        label: aboutTxt,
        data: app_info,
        textClass: "default-text",
      },
      {
        label: appCategoryTxt,
        data: [
          ...new Set(app_categories?.map((category) => category.category)),
        ],
        isShowTag: true,
      },
      {
        label: supportEmailTxt,
        data: support_email,
        icon: (
          <Box display="flex">
            <a
              href={`mailto:${support_email}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: "pointer" }}
            >
              {" "}
              <WalletSymbols symbol="mail" sx={{ color: "#aba3a3" }} />
            </a>
            <CustomTooltip
              arrow={false}
              placement="top"
              text={
                <>
                  <CheckCircleSharpIcon />
                  <span>Copied</span>{" "}
                </>
              }
              open={emailCopied}
            >
              <Box sx={{ cursor: "pointer" }}>
                <WalletSymbols
                  symbol="content_copy"
                  action={(e) => {
                    e.stopPropagation();
                    copyToClipboard(support_email, setEmailCopied);
                  }}
                  sx={{ color: "#aba3a3" }}
                />
              </Box>
            </CustomTooltip>
          </Box>
        ),
      },
    ],
    [
      shareable_url,
      app_info,
      app_categories,
      support_email,
      copied,
      emailCopied,
    ]
  );

  useEffect(() => {
    if (id) {
      fetchAppDetails(id);
    }
  }, [id]);

  const fetchAppDetails = async (appId) => {
    try {
      setIsLoading(true);
      const response = await callAPIInterface(
        "GET",
        `/manage-app/apps/${appId}`
      );
      setAppInfo(response);

      if (!response?.is_app_installed) {
        await commonAnalyticsAPI({ data: response, type: EVENT_TYPES.VIEW });
      }
    } catch (err) {
      console.log("Error while fetching app details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const appDetailLoader = () => {
    return (
      <Box className="app-details-wrapper" height="100%">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          padding="20px"
        >
          <CommonSkeleton
            variant="rectangular"
            height="56px"
            width="56px"
            borderRadius="14px"
          />
          <CommonSkeleton width="100px" />
          <CommonSkeleton />

          <Box
            width="100%"
            boxSizing="border-box"
            bgcolor="#fff"
            borderRadius={2}
            p={2}
            m="16px 0"
          >
            {Array(2)
              .fill("")
              .map((detail, idx) => {
                return (
                  <React.Fragment key={detail?.label}>
                    <Box padding="12px 0px">
                      <Box display="flex" justifyContent="space-between">
                        <CommonSkeleton width="100px" />
                      </Box>
                      <Box>
                        <CommonSkeleton />
                      </Box>
                    </Box>
                    {Array(2).fill("").length - 1 !== idx && <Divider />}
                  </React.Fragment>
                );
              })}
          </Box>
        </Box>
      </Box>
    );
  };

  const displayCategories = (categories) => {
    return categories?.map((det) => {
      return (
        <Tag
          key={det}
          text={det}
          variant="grey"
          sx={{
            "& .MuiChip-label": {
              color: "#848B9E !important",
              padding: "5px",
            },
            "&.MuiChip-root": {
              padding: "10px",
              borderRadius: "4px",
              height: "100%",
            },
            marginRight: "5px",
            marginTop: "10px",
          }}
        />
      );
    });
  };

  const handleInstallApp = async (clientId) => {
    try {
      setInstallAppLoader(true);
      const response = await callAPIInterface(
        "POST",
        "/manage-app/apps-account",
        {
          client_id: clientId,
        }
      );

      setAppInfo({ ...appInfo, is_app_installed: true });

      if (!response?.app_id) {
        console.log("Invalid response from install app API");
        return;
      }

      const { client_id } = response.app_id;

      if (myApps?.length < 5) {
        const response = await callAPIInterface(
          "GET",
          `/manage-app/apps-account`
        );
        dispatch(setMyAppsData(response?.data));
      }
      //Updates the app information and Redux state for collections and app lists to reflect the installation.
      const updatedCollections = collectionData?.map((collection) => {
        return {
          ...collection,
          app_list: collection?.app_list?.map((app) => {
            if (app.client_id === client_id) {
              return {
                ...app,
                is_app_installed: true,
              };
            } else {
              return app;
            }
          }),
        };
      });

      // data in appList will be in form of array while setting from category detail page and is object while setting from collection detail page.
      const updatedAppList = Array.isArray(appList)
        ? appList?.map((app) => {
            if (app?.client_id === client_id) {
              return {
                ...app,
                is_app_installed: true,
              };
            } else {
              return app;
            }
          })
        : appList?.app_list?.map((app) => {
            if (app?.client_id === client_id) {
              return {
                ...app,
                is_app_installed: true,
              };
            } else {
              return app;
            }
          });

      dispatch(setCollectionData(updatedCollections));
      dispatch(
        setAppList(
          Array.isArray(appList)
            ? updatedAppList
            : {
                ...appList,
                app_list: updatedAppList,
              }
        )
      );

      //open installed app
      handleOpenAppDialog({
        data: { ...response.app_id, is_app_installed: true },
      });

      setIsAppInstalled(true);
    } catch (err) {
      console.log("Error while fetching apps-account API", err);
    } finally {
      setInstallAppLoader(false);
    }
  };

  const handleAction = () => {
    isAppInstalled
      ? handleOpenAppDialog({
          data: appInfo,
        })
      : handleInstallApp(client_id);
  };

  return (
    <Dialog
      isOpenModal={isAppDetailOpen}
      setIsOpenModal={() => dispatch(openAppDetailModal(false))}
      modalHeader={appDetail}
      directionToOpen={"right"}
      handleAction={() => dispatch(openAppDetailModal(false))}
    >
      {isLoading ? (
        appDetailLoader()
      ) : (
        <>
          <Box className="app-details-wrapper">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              padding="20px"
            >
              <Avatar
                src={imgSrc || loaderImg}
                alt={display_name}
                className="list-avatar"
                variant="rounded"
                onError={() => {
                  setImgSrc(loaderImg);
                }}
              />
              <Text size={22} font="bold" mb={1}>
                {display_name}
              </Text>
              <Text className="grey-text" size={16} font="regular" mb={0.5}>
                {description}
              </Text>

              <Box
                width="100%"
                boxSizing="border-box"
                bgcolor="#fff"
                borderRadius={2}
                p={2}
                m="16px 0"
              >
                {appDetailsInfo.map((detail, idx) => {
                  return (
                    !!detail?.data?.length && (
                      <React.Fragment key={detail?.label}>
                        <Box
                          padding="12px 0px"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          className="apps-details"
                        >
                          <Box>
                            <Box display="flex" justifyContent="space-between">
                              <Text
                                size={14}
                                font="regular"
                                className={"grey-text"}
                              >
                                {detail.label}
                              </Text>
                            </Box>

                            {detail?.isShowTag ? (
                              displayCategories(detail?.data)
                            ) : (
                              <Text
                                size={14}
                                font="regular"
                                className={detail?.textClass || "blue-text"}
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {detail.data}
                              </Text>
                            )}
                          </Box>

                          {detail?.icon}
                        </Box>
                      </React.Fragment>
                    )
                  );
                })}
              </Box>
            </Box>
          </Box>
          <Text
            size={12}
            font="regular"
            className="grey-text"
            align="center"
            sx={{
              padding: "10px 20px",
              textAlign: "left",
            }}
          >
            {launchAppTerms[0]}
            <CustomLink
              size="small"
              href={
                process.env.REACT_APP_PROMO_WEBSITE +
                "/terms-of-use-for-speed-mini-app"
              }
              target="_blank"
              sx={{ fontSize: "12px !important" }}
            >
              {launchAppTerms[1]}
            </CustomLink>
          </Text>

          <Box
            className="options-footer-btn"
            sx={{ borderTop: "1px solid #f6f3f3 !important" }}
          >
            <Button
              variant="contained"
              color="primary"
              label={btnLabel}
              fullWidth
              onClick={handleAction}
              loading={installAppLoader}
              disabled={removedAppStatus.includes(status)}
            />
          </Box>
        </>
      )}
    </Dialog>
  );
}

export default AppDetailsDialog;
